/**
 * Client-side plan statistics.
 *
 * Block populations are fetched once per session (GET
 * /v1/tenant/block-populations) and kept in the painting store; every paint
 * stroke recomputes per-district totals locally so the palette stays live
 * with zero server round-trips. Ideal population = tenant total / district
 * count.
 */
export interface DistrictStat {
  district: number;
  population: number;
  /** Signed percent deviation from ideal, e.g. +2.35 or -0.80. */
  deviationPct: number;
}

/**
 * Sum block populations per district from the assignment map.
 * Blocks without a population entry count as 0; unassigned blocks are
 * excluded from every district total.
 */
export function computeDistrictStats(
  assignments: Record<string, number>,
  populations: Record<string, number> | null,
  districtCount: number,
): DistrictStat[] {
  const populationsByDistrict = new Array<number>(districtCount).fill(0);
  if (populations) {
    for (const [geoid, district] of Object.entries(assignments)) {
      if (district >= 0 && district < districtCount) {
        populationsByDistrict[district] =
          (populationsByDistrict[district] ?? 0) + (populations[geoid] ?? 0);
      }
    }
  }
  const total = populations
    ? Object.values(populations).reduce((sum, pop) => sum + pop, 0)
    : 0;
  const ideal = districtCount > 0 ? total / districtCount : 0;
  return populationsByDistrict.map((population, district) => ({
    district,
    population,
    deviationPct: ideal > 0 ? ((population - ideal) / ideal) * 100 : 0,
  }));
}

/** +2.35% / −0.80% with an explicit sign. */
export function formatDeviation(pct: number): string {
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}

export function formatPopulation(population: number, locale = 'en-US'): string {
  return Math.round(population).toLocaleString(locale);
}
